import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { companyInfo } from '../constants';
import { contactAPI } from '../services/api';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await contactAPI.submit(formData);
      sonnerToast.success("Başarılı!", {
        description: result.message,
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      sonnerToast.error("Hata!", {
        description: error.detail || "Mesaj gönderilemedi. Lütfen tekrar deneyin.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">İletişime Geçin</h2>
          <p className="text-lg text-gray-600">
            Size yardımcı olmak için buradayız. Sorularınızı bize iletin.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Mesaj Gönderin</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-gray-700 mb-2 block">
                  Ad Soyad *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full"
                  placeholder="Adınız ve soyadınız"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700 mb-2 block">
                  E-posta *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full"
                  placeholder="ornek@email.com"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-gray-700 mb-2 block">
                  Telefon
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full"
                  placeholder="05XX XXX XX XX"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-700 mb-2 block">
                  Mesajınız *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full"
                  placeholder="Mesajınızı buraya yazın..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                {isSubmitting ? (
                  <span>Gönderiliyor...</span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Send className="w-5 h-5 mr-2" />
                    Mesaj Gönder
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="bg-gradient-to-br from-sky-50 to-amber-50 rounded-2xl p-8 border border-sky-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">İletişim Bilgileri</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-sky-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Telefon</p>
                    <a href={`tel:${companyInfo.phone1.replace(/\s/g, '')}`} className="text-sky-600 hover:text-sky-700 block">
                      {companyInfo.phone1}
                    </a>
                    <a href={`tel:${companyInfo.phone2.replace(/\s/g, '')}`} className="text-sky-600 hover:text-sky-700 block">
                      {companyInfo.phone2}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">E-posta</p>
                    <a href={`mailto:${companyInfo.email}`} className="text-sky-600 hover:text-sky-700">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Adres</p>
                    <p className="text-gray-700">{companyInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-slate-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Çalışma Saatleri</p>
                    <p className="text-gray-700">Pazartesi - Cuma</p>
                    <p className="text-gray-700">09:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-gray-200 rounded-2xl overflow-hidden h-64 border border-gray-300">
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-sky-100 to-amber-100">
                <div className="text-center">
                  <MapPin className="w-16 h-16 text-sky-600 mx-auto mb-4" />
                  <p className="text-gray-700 font-semibold">Leylekpınar Mah. Barış İş Merkezi</p>
                  <p className="text-gray-600">Merkez, Ağrı</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;